import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useArticles } from "@/hooks/useArticles";
import { toNewsItems } from "@/lib/articleAdapter";
import { filterBySearch } from "@/lib/articleSearch";
import { AdSidebar, type AdPlaceholder } from "@/components/feature/SponsorAds";

const categoryColors: Record<string, string> = {
  Destinos: "bg-emerald-600",
  Hoteles: "bg-rose-500",
  Inversión: "bg-amber-500",
  Asociaciones: "bg-indigo-500",
};

const turismoSubcategories = ["Destinos", "Hoteles", "Inversión", "Asociaciones"];

// Los mismos espacios que antes estaban al final de la página, ahora a la derecha de las noticias.
const turismoAdPlaceholders: AdPlaceholder[] = [
  { title: "Hoteles & Resorts", desc: "Llega a viajeros que planifican su próxima escapada al Caribe", accentClass: "from-[#7c2d12] to-[#c2410c]" },
  { title: "Vuelos & Aerolíneas", desc: "Conecta con viajeros buscando vuelos hacia RD", accentClass: "from-[#0B1F3A] to-[#1a3a6b]" },
  { title: "Renta de Vehículos", desc: "Turistas que necesitan movilidad en sus destinos", accentClass: "from-[#14532d] to-[#166534]" },
  { title: "Seguros de Viaje", desc: "Viajeros que buscan protección para sus vacaciones", accentClass: "from-[#1e1b4b] to-[#3730a3]" },
];

export default function TurismoNoticias() {
  const [activeFilter, setActiveFilter] = useState<string>("Todas");
  const [search, setSearch] = useState("");
  const { articles, loading } = useArticles("Turismo");
  const newsItems = useMemo(() => toNewsItems(articles), [articles]);

  const filteredNews = useMemo(() => {
    let base = newsItems;

    if (activeFilter !== "Todas") {
      // Filter by subcategory stored in the article's tags or the category itself
      base = base.filter((n) => {
        const article = articles.find((a) => a.id === n.id);
        return article?.subcategory === activeFilter || n.category === activeFilter;
      });
    }

    const byId = new Map(articles.map((a) => [a.id, a]));
    base = filterBySearch(base, search, (n) => byId.get(n.id));

    return base;
  }, [newsItems, articles, activeFilter, search]);

  const displayNews = filteredNews.slice(0, 9);

  return (
    <section className="py-20 bg-[#f8f9fb]">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-6 bg-rose-500 rounded-full" />
              <span className="text-rose-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Noticias
              </span>
            </div>
            <h2 className="text-[#071529] font-extrabold text-3xl md:text-4xl" style={{ fontFamily: "'Sora', sans-serif" }}>
              Noticias del Sector Turístico
            </h2>
            <p className="text-slate-500 text-sm mt-2 max-w-2xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Destinos, hoteles, inversión y asociaciones. Todo lo que mueve el turismo dominicano.
            </p>
          </div>
          <Link
            to="/actualidad?categoria=Turismo"
            className="flex items-center gap-2 text-sm font-semibold text-[#071529] hover:text-rose-600 transition-colors cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Ver todas las noticias
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar noticias de turismo..."
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {["Todas", ...turismoSubcategories].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === cat
                    ? "bg-[#071529] text-white"
                    : "bg-white text-slate-500 hover:text-[#071529] border border-slate-200"
                }`}
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3,4,5,6].map((i) => (
                  <div key={i} className="h-72 bg-slate-200 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : displayNews.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                  <i className="ri-newspaper-line text-slate-400 text-2xl" />
                </div>
                {search ? (
                  <>
                    <p className="text-slate-400 text-sm">No se encontraron noticias.</p>
                    <button
                      onClick={() => setSearch("")}
                      className="mt-3 text-rose-600 text-sm font-medium hover:underline cursor-pointer"
                    >
                      Limpiar búsqueda
                    </button>
                  </>
                ) : (
                  <p className="text-slate-400 text-sm">
                    {activeFilter !== "Todas"
                      ? `Próximamente noticias de ${activeFilter}`
                      : "Próximamente noticias del sector turístico"}
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayNews.map((noticia) => {
                  const article = articles.find((a) => a.id === noticia.id);
                  const subcatLabel = article?.subcategory || noticia.category;
                  return (
                    <Link
                      key={noticia.id}
                      to={`/articulo/${noticia.slug}`}
                      className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-200 transition-all group cursor-pointer block"
                    >
                      <div className="w-full h-48 overflow-hidden bg-slate-100">
                        <img
                          src={noticia.image}
                          alt={noticia.title}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=Caribbean%20tourism%20travel%20tropical%20destination%20Dominican%20Republic%20professional%20photography&width=800&height=500&seq=tur-fallback&orientation=landscape";
                          }}
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <span className={`inline-block text-[10px] font-bold px-2.5 py-1 rounded-full mb-0 text-white ${categoryColors[subcatLabel] || categoryColors[noticia.category] || "bg-orange-500"}`}>
                            {subcatLabel}
                          </span>
                          {noticia.isInternational && (
                            <span className="inline-flex items-center gap-1 text-blue-600 text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100">
                              <i className="ri-earth-line" />
                              Internacional
                            </span>
                          )}
                        </div>
                        <h3
                          className="text-[#071529] font-bold text-base leading-snug mb-2 group-hover:text-rose-600 transition-colors"
                          style={{ fontFamily: "'Sora', sans-serif" }}
                        >
                          {noticia.title}
                        </h3>
                        <p className="text-slate-500 text-xs leading-relaxed line-clamp-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                          {noticia.excerpt}
                        </p>
                        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
                          <span className="text-slate-400 text-xs" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{noticia.date}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Publicidad (columna derecha) */}
          <aside className="lg:col-span-1">
            <AdSidebar placement="Turismo" placeholders={turismoAdPlaceholders} max={4} />
          </aside>
        </div>
      </div>
    </section>
  );
}